export const API_VERSION = "v1" as const;
export const CORE_CONTRACT_VERSION = 1 as const;

export type ForecastState = "clear" | "likely" | "open" | "unavailable";
export type ForecastReason =
  | "vessel_approach"
  | "scheduled_river_transit"
  | "upstream_progress";
export type PhysicalBridgeState = "down" | "up" | "unknown";
export type Freshness = "live" | "delayed" | "stale" | "unknown";
export type VesselDirection = "inbound" | "outbound" | "holding" | "unknown";
export type BridgeVesselPhase = "approaching" | "waiting" | "clearing";
export const VESSEL_CORRIDORS = [
  "miami_river",
  "north_approach",
  "government_cut",
  "south_approach",
] as const;
export type VesselCorridor = (typeof VESSEL_CORRIDORS)[number];

export interface PublicBridge {
  id: BridgeId;
  name: string;
  shortName: string;
  state: PhysicalBridgeState;
  freshness: Freshness;
  observedAt: string | null;
  lastConfirmedAt: string | null;
}

export interface PublicVesselSummary {
  id: string;
  name: string | null;
  type: VesselKind;
  corridor: VesselCorridor;
  direction: VesselDirection;
  /** Signed along-channel metres: 0 at Brickell, positive upriver, negative seaward. */
  sMeters: number;
  /** Why this live track belongs on the Brickell decision surface. */
  bridgePhase: BridgeVesselPhase;
  /** Brickell has previously been confirmed raised for this vessel. */
  knownOpener: boolean;
  progress: number;
  /** 0 at the river mouth; 1 at this corridor branch's outer/upriver terminus. */
  corridorProgress: number;
  /** Along-channel distance to the Brickell Avenue span. */
  distanceToBrickellMeters: number | null;
  speedKnots: number | null;
  courseDegrees: number | null;
  etaMinutes: number | null;
  observedAt: string;
}

export interface PublicVesselDetail extends PublicVesselSummary {
  callSign: string | null;
  imo: string | null;
  destination: string | null;
  lengthMeters: number | null;
  beamMeters: number | null;
  passages: Array<{
    bridgeId: BridgeId;
    bridgeName: string;
    crossedAt: string;
  }>;
}

/** A probable multi-vessel movement inferred from sustained AIS tracks. */
export interface PublicVesselGroup {
  id: string;
  kind: "likely_tow";
  tugIds: string[];
  towIds: string[];
  observedAt: string;
  /** Inferred route offsets; independent of the reported member fixes. */
  memberOffsetsMeters?: Record<string, number> | undefined;
}

export interface PublicVesselActivity {
  /** Unique vessels with a usable position received during the active window. */
  activeTracks: number;
  /** Vessels currently shown as relevant to the Brickell passage. */
  approachingBridge: number;
  status: "receiving" | "connected" | "connecting" | "reconnecting" | "unavailable" | "disabled";
  issue:
    | "upgrade_timeout"
    | "upgrade_failed"
    | "upgrade_rejected"
    | "upgrade_accept_failed"
    | "subscription_timeout"
    | "subscription_send_failed"
    | "stream_idle"
    | "service_error"
    | "decode_failed"
    | "unsupported_frame"
    | "socket_closed"
    | "socket_error"
    | null;
  freshness: Freshness;
  updatedAt: string | null;
}

export type CurrentWeatherCondition =
  | "clear"
  | "mainly_clear"
  | "partly_cloudy"
  | "overcast"
  | "fog"
  | "drizzle"
  | "freezing_drizzle"
  | "rain"
  | "freezing_rain"
  | "snow"
  | "showers"
  | "thunderstorm"
  | "unknown";

export interface PublicWeatherOutlook {
  condition: CurrentWeatherCondition;
  conditionsValidAt: string | null;
  isDay: boolean | null;
  temperatureF: number | null;
  precipitationRateMmPerHour: number | null;
  cloudCoverPercent: number | null;
  cloudCoverLowPercent: number | null;
  cloudCoverMidPercent: number | null;
  cloudCoverHighPercent: number | null;
  windSpeedMph: number | null;
  windDirectionDegrees: number | null;
  windGustMph: number | null;
  directNormalIrradianceWm2: number | null;
  diffuseRadiationWm2: number | null;
  state: "dry" | "rain_expected" | "rain_window_now" | "unavailable";
  windowStartsAt: string | null;
  windowEndsAt: string | null;
  amountMm: number | null;
  rainChancePercent: number | null;
  freshness: Freshness;
  updatedAt: string | null;
}

export interface PublicRoadTrafficRoad {
  /** Stable path identifier shared with the rendered street network. */
  pathId: string;
  condition: "free_flow" | "moderate" | "heavy" | "severe";
  congestionPercent: number;
  speedMph: number | null;
  freeFlowSpeedMph: number | null;
  observedSegments: number;
}

/** Area summary plus street-level flow evidence for the Brickell scene. */
export interface PublicRoadTraffic {
  /** Identifies whether values came from TomTom Orbis flow or the deterministic model. */
  source: "tomtom" | "modeled";
  condition: "free_flow" | "moderate" | "heavy" | "severe" | "unavailable";
  /** Length-weighted speed loss relative to free flow, normalized to 0–100. */
  congestionPercent: number | null;
  speedMph: number | null;
  freeFlowSpeedMph: number | null;
  /** Number of TomTom flow features represented by the aggregate. */
  observedSegments: number;
  roads: PublicRoadTrafficRoad[];
  freshness: Freshness;
  updatedAt: string | null;
}

export interface PublicSnapshot {
  version: typeof API_VERSION;
  dataMode: "demo" | "live";
  sequence: number;
  generatedAt: string;
  experience: {
    livingRiverEnabled: boolean;
  };
  forecast: {
    state: ForecastState;
    horizonMinutes: 30;
    openingWindow: {
      earliestMinutes: number;
      latestMinutes: number;
    } | null;
    confidencePercent: number | null;
    reason: {
      kind: ForecastReason;
      vesselName: string | null;
    } | null;
    freshness: Freshness;
    updatedAt: string;
  };
  bridges: PublicBridge[];
  weather: PublicWeatherOutlook;
  roadTraffic: PublicRoadTraffic;
  vesselActivity: PublicVesselActivity;
  vessels: PublicVesselSummary[];
  vesselGroups: PublicVesselGroup[];
}

export interface PublicBridgeEvent {
  id: string;
  bridgeId: BridgeId;
  bridgeName: string;
  state: Exclude<PhysicalBridgeState, "unknown">;
  occurredAt: string;
}

export interface EventPage {
  events: PublicBridgeEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

export type LiveServerMessage =
  | { type: "snapshot"; snapshot: PublicSnapshot }
  | { type: "update"; sequence: number; snapshot: PublicSnapshot }
  | { type: "resync"; expectedSequence: number };

export type VesselKind =
  | "tug"
  | "tow"
  | "cargo"
  | "tanker"
  | "sailboat"
  | "yacht"
  | "passenger"
  | "fishing"
  | "pilot"
  | "generic";

export const BRIDGE_IDS = [
  "brickell",
  "sw-2-ave",
  "sw-1-st",
  "w-flagler",
  "nw-5-st",
  "nw-12-ave",
  "nw-17-ave",
  "nw-22-ave",
  "nw-27-ave",
] as const;

export type BridgeId = (typeof BRIDGE_IDS)[number];
